import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import "../employer/css/emp_layout.css";

export default function JSCreateMarketPlaceListing() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [checkingSub, setCheckingSub] = useState(true);
  const [countiesList, setCountiesList] = useState([]);
  
  // File upload state slots
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Unified Form State Engine
  const [formData, setFormData] = useState({
    listingType: "Product",
    title: "",
    description: "",
    price: "",
    category: "Skincare",
    stockQuantity: "1",
    locationCity: "Nairobi",
    rentalFrequency: "Monthly",
    availableFrom: new Date().toISOString().split("T")[0]
  });

  useEffect(() => {
    const checkAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/signin"); return; }
      
      try {
        const response = await fetch(`${API_ENDPOINTS.MARKETPLACE}/listings?page=1`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const [countyRes] = await Promise.all([
            fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`, {
              headers: { "Authorization": `Bearer ${token}` }
            })            
          ]);
        
          if (countyRes.ok) setCountiesList(await countyRes.json());
        }
      } catch (err) {
        console.error("Subscription guard error:", err);
      } finally {
        setCheckingSub(false);
      }
    };
    checkAccess();
  }, [navigate]);

  // Handle local image staging & thumbnail processing
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Safety check: Total maximum cap of 5 files
    if (selectedImages.length + files.length > 5) {
      alert("Maximum limit exceeded: You can only upload up to 5 photos per listing.");
      return;
    }

    const updatedImages = [...selectedImages, ...files];
    setSelectedImages(updatedImages);

    // Map files to local URL strings for temporary UI previews
    const objectivePreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...objectivePreviews]);
  };

  const removeStagedImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      setSubmitting(true);
      
      // Constructing FormData payload to support multi-part binary file uploads
      const dataPayload = new FormData();
      
      dataPayload.append("listingType", formData.listingType);
      dataPayload.append("title", formData.title);
      dataPayload.append("description", formData.description);
      dataPayload.append("price", formData.price);
      dataPayload.append("category", formData.category);
      
      if (formData.listingType === "Product") {
        dataPayload.append("stockQuantity", formData.stockQuantity);
      } else {
        dataPayload.append("locationCity", formData.locationCity);
        dataPayload.append("rentalFrequency", formData.rentalFrequency);
        dataPayload.append("availableFrom", formData.availableFrom);
      }

      // Append raw binary images directly to the form bundle
      selectedImages.forEach((imageFile) => {
        dataPayload.append("images", imageFile);
      });

      const response = await fetch(`${API_ENDPOINTS.MARKETPLACE}/createlisting`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
          // Note: Content-Type header must be omitted when sending FormData so the browser generates boundaries natively!
        },
        body: dataPayload
      });

      if (response.ok) {
        alert("Listing successfully added with attachments to the B2B Marketplace!");
        navigate("/marketplace");
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to finalize showroom catalog.");
      }
    } catch (err) {
      console.error("Submission processing crash:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSub) {
    return (
      <JSLayout title="Verifying Account Status..." titleEm="Please wait ◈">
        <div className="emd-loading-container"><div className="si-spinner"></div></div>
      </JSLayout>
    );
  }

  return (
    <JSLayout title="Launch a New" titleEm="Marketplace Post ◈">
      <div className="emd-fade" style={{ maxWidth: "700px", margin: "0 auto" }}>
        
        <form onSubmit={handleSubmit} className="emd-panel-card" style={{ padding: "2.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          
          {/* Dynamic Type Selector */}
          <div>
            <label className="emd-stat-label" style={{ marginBottom: "0.5rem", display: "block" }}>What are you listing?</label>
            <div style={{ display: "flex", gap: "1rem" }}>
              {[
                { type: "Product", label: "🧴 Premium Beauty Product" },
                { type: "RentalSpace", label: "🏢 Rental Space Lease" }
              ].map(opt => (
                <label 
                  key={opt.type} 
                  className={`emd-listing-card`} 
                  style={{
                    flex: 1, cursor: "pointer", padding: "1rem", textAlign: "center",
                    border: formData.listingType === opt.type ? "2px solid var(--color-primary)" : "1px solid #E0E0E0",
                    background: formData.listingType === opt.type ? "var(--color-primary-soft-bg)" : "transparent",
                    color: formData.listingType === opt.type ? "#000" : "#bbb",
                    transition: "all 0.2s"
                  }}
                >
                  <input 
                    type="radio" name="listingType" value={opt.type} 
                    checked={formData.listingType === opt.type} onChange={handleChange} style={{ display: "none" }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Standard Fields */}
          <div>
            <label className="emd-stat-label">Listing Title</label>
            <input 
              type="text" name="title" required value={formData.title} onChange={handleChange}
              placeholder={formData.listingType === "Product" ? "e.g., Organic Argan Oil Bulk Supply" : "e.g., Fully Equipped Luxury Salon Chair for Rent"}
              className="emp-input"
            />
          </div>

          <div>
            <label className="emd-stat-label">Item / Space Description</label>
            <textarea 
              name="description" required rows="4" value={formData.description} onChange={handleChange}
              placeholder="Outline specific traits, sizing details, dimensions, brand metrics, or open amenities included..."
              className="emp-textarea"
            />
          </div>

          {/* Media Asset Manager Upload Box */}
          <div>
            <label className="emd-stat-label">Showroom Gallery Photos (Up to 5 images)</label>
            <div style={{
              border: "2px dashed var(--color-primary-dark)", borderRadius: "8px", padding: "1.5rem", 
              textAlign: "center", background: "rgba(212, 175, 55, 0.03)", position: "relative"
            }}>
              <input 
                type="file" accept="image/*" multiple onChange={handleImageChange}
                disabled={selectedImages.length >= 5}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
              />
              <span style={{ color: "var(--color-primary-light)", fontWeight: "500" }}>＋ Click or Drag to Drop Photos Here</span>
              <p style={{ margin: "5px 0 0 0", fontSize: "0.8rem", color: "#888" }}>Selected ({selectedImages.length} / 5)</p>
            </div>

            {/* Render Image Thumbnail Previews */}
            {imagePreviews.length > 0 && (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
                {imagePreviews.map((url, index) => (
                  <div key={index} style={{ position: "relative", width: "85px", height: "85px", borderRadius: "6px", overflow: "hidden", border: "1px solid #444" }}>
                    <img src={url} alt="Staged item look" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button 
                      type="button" onClick={() => removeStagedImage(index)}
                      style={{
                        position: "absolute", top: "2px", right: "2px", background: "rgba(0,0,0,0.7)", 
                        color: "#fff", border: "none", borderRadius: "50%", cursor: "pointer", width: "18px", height: "18px", fontSize: "0.7rem"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label className="emd-stat-label">
                {formData.listingType === "Product" ? "Price (KES)" : `Rent Price (${formData.rentalFrequency})`}
              </label>
              <input 
                type="number" name="price" required min="0" value={formData.price} onChange={handleChange}
                placeholder="0.00" className="emp-input"
              />
            </div>
            
            <div>
              <label className="emd-stat-label">Classification Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className="emp-select">
                <option value="Skincare">Skincare 🧴</option>
                <option value="Haircare">Haircare / Extensions 💇‍♀️</option>
                <option value="Cosmetics">Cosmetics / Makeup 💄</option>
                <option value="SalonSpace">Salon Space / Chair Lease 🏢</option>
                <option value="Equipment">Heavy Salon Equipment ⚡</option>
              </select>
            </div>
          </div>

          {/* Conditional Input Fields Based on Listing Type */}
          {formData.listingType === "Product" ? (
            <div>
              <label className="emd-stat-label">Initial Available Stock Quantity</label>
              <input type="number" name="stockQuantity" required min="1" value={formData.stockQuantity} onChange={handleChange} className="emp-input" />
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <label className="emd-stat-label">Location (City)</label>
                  <select className="emp-select" value={formData.locationCity} onChange={handleChange} name="locationCity" required>
                    <option value="">Select your Location...</option>
                    {countiesList.map(county => (
                      <option key={county.id} value={county.name}>{county.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="emd-stat-label">Lease Rate Frequency</label>
                  <select className="emp-select" value={formData.rentalFrequency} onChange={handleChange} name="rentalFrequency">
                    <option value="Hourly">Hourly Rate 🕒</option>
                    <option value="Daily">Daily Rate ☀️</option>
                    <option value="Monthly">Monthly Lease 🗓️</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="emd-stat-label">Lease Availability Start Date</label>
                <input type="date" name="availableFrom" required value={formData.availableFrom} onChange={handleChange} className="emp-input" />
              </div>
            </>
          )}

          <div className="emd-listing-divider" style={{ margin: "1rem 0" }} />

          {/* Form Action Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
            <button type="button" className="emp-btn-ghost" disabled={submitting} onClick={() => navigate("/marketplace")}>
              Cancel
            </button>
            <button 
              type="submit" className="emp-btn-primary" disabled={submitting}
              style={{ background: "linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-primary-dark) 100%)", border: "none" }}
            >
              {submitting ? "Publishing Catalog..." : "Publish to Marketplace 🚀"}
            </button>
          </div>

        </form>
      </div>
    </JSLayout>
  );
}